import { IconClock } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import { aiGenerationCreditCosts } from "@forento/core/models/ai"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import InputField, { InputTextArea } from "~/components/InputField"
import Link from "~/components/Link"
import { DefaultModal, ModalButtons, ModalContentPadding, ModalHeader, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { captureForentoAiEvent, getBuyAiCreditsUrl, useForentoAiModalTracking } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

import ChapterGenerationStatus from "./ChapterGenerationStatus"

type Props = { isOpen: boolean; courseId: number; onClose(): void; onComplete(): void }

const AiChapterGeneration: FC<Props> = ({ isOpen, courseId, onClose, onComplete }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!

	const [requestId, setRequestId] = useState<number | null>(null)
	const [suggestedTitle, setSuggestedTitle] = useState("")
	const [learningGoal, setLearningGoal] = useState("")
	const [includeQuizPages, setIncludeQuizPages] = useState(false)
	const [includeInputPages, setIncludeInputPages] = useState(false)

	const chapterStatus = query.ai.getChapterStatus.useQuery(requestId ?? -1, {
		enabled: requestId !== null,
		refetchInterval: q =>
			q.state.data !== null && q.state.data?.status !== "completed" && q.state.data?.status !== "error"
				? 3000
				: false,
	})

	const generate = query.ai.generateChapter.useMutation({
		async onError(error) {
			console.error(error)
			captureForentoAiEvent("forento_ai_generation_failed", { type: "chapter" })
			await alert.show(
				"Failed to generate chapter",
				error.message || "An error occurred while generating the chapter. Please try again.",
			)
		},
		onSuccess(data) {
			setRequestId(data.requestId)
		},
	})

	const isCompleted = chapterStatus.data?.status === "completed"
	const isFailed = chapterStatus.data?.status === "error"
	const isFinished = isCompleted || isFailed

	useEffect(() => {
		if (!isCompleted) return
		captureForentoAiEvent("forento_ai_generation_succeeded", { type: "chapter" })
		void user.reload()
	}, [isCompleted])

	useEffect(() => {
		if (isFailed) captureForentoAiEvent("forento_ai_generation_failed", { type: "chapter" })
	}, [isFailed])

	const resetPromptFields = () => {
		setSuggestedTitle("")
		setLearningGoal("")
		setIncludeQuizPages(false)
		setIncludeInputPages(false)
	}

	const handleClose = () => {
		if (isCompleted) {
			onComplete()
		}
		// Closing while the worker is still generating keeps the request id, since dropping it stops the
		// status query and the balance the worker charges would never be reloaded.
		if (isFinished) {
			setRequestId(null)
			resetPromptFields()
		}
		onClose()
	}

	const chapterCost = aiGenerationCreditCosts.chapter
	const hasEnoughCredits = platform.aiCreditBalance >= chapterCost
	useForentoAiModalTracking({
		type: "chapter",
		isFormVisible: isOpen && requestId === null,
		creditBalance: platform.aiCreditBalance,
		cost: chapterCost,
	})

	const handleGenerate = () => {
		if (!hasEnoughCredits || generate.isPending) return
		captureForentoAiEvent("forento_ai_generation_submitted", { type: "chapter" })
		generate.mutate({ courseId, suggestedTitle, learningGoal, includeQuizPages, includeInputPages })
	}

	return (
		<DefaultModal onClose={handleClose} width={560} isOpen={isOpen}>
			<ModalHeader>
				<ModalTitle>Generate next chapter with Forento AI</ModalTitle>
			</ModalHeader>
			<ModalContentPadding>
				{requestId !== null ? (
					<>
						<div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
							<p className="text-sm text-amber-900">
								<span className="font-semibold">Forento AI is in beta.</span> Results may vary and you
								may need to edit the generated content.
							</p>
						</div>
						<ChapterGenerationStatus requestId={requestId} />
						{!isCompleted && (
							<div className="mt-4 flex items-center gap-1.5 rounded-lg border border-blue-100 bg-blue-50 p-3">
								<IconClock className="size-4 text-blue-600" />
								<span className="text-sm text-blue-900">
									This may take a minute. You can safely close this window — generation continues in
									the background.
								</span>
							</div>
						)}
					</>
				) : (
					<>
						<div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
							<p className="text-sm text-amber-900">
								<span className="font-semibold">Forento AI is in beta.</span> Results may vary and you
								may need to edit the generated content.
							</p>
						</div>
						<div className="rounded-lg border border-gray-200 bg-white p-4">
							<div className="mb-3 flex items-center justify-between">
								<div className="text-sm font-semibold text-gray-900">AI credits</div>
								<div className="text-xs font-medium text-gray-500">
									Cost: {toNumberFormat(chapterCost)} credits
								</div>
							</div>
							<div className="mb-1.5 flex items-center justify-between text-sm">
								<span className="text-gray-700">Available balance</span>
								<span
									className={cn(
										"font-semibold text-gray-900",
										platform.aiCreditBalance < chapterCost && "text-red-600",
									)}
								>
									{toNumberFormat(platform.aiCreditBalance)} credits
								</span>
							</div>
							<div className="h-2 overflow-hidden rounded-full bg-gray-200">
								<div
									className={cn(
										"bg-primary h-full rounded-full transition-all",
										platform.aiCreditBalance < chapterCost && "bg-red-500",
									)}
									style={{
										width: `${Math.min((platform.aiCreditBalance / chapterCost) * 100, 100)}%`,
									}}
								/>
							</div>
						</div>
						{hasEnoughCredits && (
							<>
								<p className="mt-4 text-sm text-gray-600">
									Forento AI will analyze your existing chapters and generate the next chapter with
									matching style and content flow.
								</p>
								<div className="mt-4 flex flex-col gap-4">
									<InputField
										label="Suggested title"
										value={suggestedTitle}
										onChange={setSuggestedTitle}
										placeholder="e.g., Applying the fundamentals"
										maxCharacters={120}
									/>
									<InputTextArea
										label="Learning goal for the chapter"
										value={learningGoal}
										onChange={setLearningGoal}
										placeholder="Describe what learners should understand, practice, or be able to do after this chapter"
										maxCharacters={500}
									/>
									<div className="rounded-lg border border-gray-200 bg-white p-3">
										<p className="mb-3 text-sm font-semibold text-gray-900">Page types</p>
										<div className="flex flex-col gap-3">
											<div>
												<Checkbox
													label="Include quiz pages"
													isChecked={includeQuizPages}
													onChange={setIncludeQuizPages}
												/>
												<p className="mt-1 pl-7 text-sm text-gray-500">
													Short multiple-choice checks between lesson pages.
												</p>
											</div>
											<div>
												<Checkbox
													label="Include input pages"
													isChecked={includeInputPages}
													onChange={setIncludeInputPages}
												/>
												<p className="mt-1 pl-7 text-sm text-gray-500">
													Free-text reflection or application prompts for students.
												</p>
											</div>
										</div>
									</div>
								</div>
							</>
						)}
						{!hasEnoughCredits && (
							<p className="mt-4 text-sm text-gray-600">
								Purchase more AI credits before generating the next chapter in{" "}
								<Link
									to={getBuyAiCreditsUrl("chapter")}
									className="text-primary font-medium hover:underline"
								>
									Billing
								</Link>
								.
							</p>
						)}
					</>
				)}
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="secondary" onClick={handleClose}>
					{isCompleted ? "Done" : "Close"}
				</Button>
				{requestId === null && (
					<Button
						variant="primary"
						isDisabled={!hasEnoughCredits || generate.isPending}
						isLoading={generate.isPending}
						onClick={handleGenerate}
					>
						Generate chapter ({toNumberFormat(chapterCost)} credits)
					</Button>
				)}
			</ModalButtons>
		</DefaultModal>
	)
}

export default AiChapterGeneration
